import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink, Phone, Mail, Heart, BookOpen, Users, Briefcase, Baby, HeartHandshake, ChevronRight } from 'lucide-react';

const resources = [
  {
    icon: BookOpen,
    title: "Self-Advocacy & Wellness",
    description: "Workshops that empower individuals to navigate healthcare, build resilience, and practice holistic self-care.",
    status: "Active"
  },
  {
    icon: Briefcase,
    title: "Workplace & Employee Training",
    description: "Targeted training for employers and staff on invisible disabilities, confidentiality standards, and creating inclusive work cultures.",
    status: "Active"
  },
  {
    icon: HeartHandshake,
    title: "Companion Care",
    description: "Friendly emotional support and companionship to assist with daily tasks and reduce isolation.",
    status: "Coming Soon"
  },
  {
    icon: Users,
    title: "Support Groups & Community",
    description: "Safe spaces for connection, shared experiences, and encouragement among those with silent disabilities.",
    status: "Active"
  },
  {
    icon: Baby,
    title: "Children's Engagement",
    description: "Creative activities through the 'Coloring the Invisible™' program helping children explore emotions, empathy, and invisible experiences.",
    status: "Active"
  },
  {
    icon: Heart,
    title: "Resource Navigation",
    description: "Personalized 1:1 support helping families navigate insurance, paperwork, appointments, and community-based resources.",
    status: "Active"
  }
];

const externalResources = [
  {
    name: "Persons with Silent Disabilities",
    url: "https://silent-disabilities.org",
    description: "501(c)(3) nonprofit dedicated to advocacy and community support"
  },
  {
    name: "PwSD Facebook Community",
    url: "https://www.facebook.com/530284626842760",
    description: "Connect with the community on Facebook"
  },
  {
    name: "PwSD Instagram",
    url: "https://www.instagram.com/silent.disabilities/",
    description: "Follow for updates and awareness content"
  }
];

export default function Resources() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-safe">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="mt-8 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">
            Resources
          </h1>
          <p className="mt-2 text-muted-foreground">
            Programs and support for individuals with silent disabilities
          </p>
        </div>

        {/* What Are Silent Disabilities */}
        <div className="mt-8 card-soft bg-secondary/50 page-enter stagger-1">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-3">
            What Are Silent Disabilities?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Silent or invisible disabilities encompass a range of conditions, including:
          </p>
          <ul className="mt-3 space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Cognitive disorders</strong> – ADHD, autism spectrum, learning disabilities</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Autoimmune diseases</strong> – Lupus, rheumatoid arthritis, MS</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Mental health conditions</strong> – Depression, anxiety, PTSD</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Chronic pain conditions</strong> – Fibromyalgia, chronic fatigue syndrome</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Neurological conditions</strong> – Epilepsy, migraines, vertigo</span>
            </li>
          </ul>
          <button
            onClick={() => navigate('/conditions')}
            className="mt-4 flex items-center gap-2 text-primary font-medium hover:underline"
          >
            <span>Explore all conditions & disclosure tips</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Dynamic Disabilities */}
        <div className="mt-4 card-soft page-enter stagger-1">
          <h3 className="font-heading font-semibold text-foreground mb-2">
            Understanding Dynamic Disabilities
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Dynamic disabilities are characterized by their fluctuating nature, with symptoms that can worsen or improve unpredictably. This variability presents unique challenges that necessitate continuous adaptation and advocacy to ensure those affected receive the support they need.
          </p>
        </div>

        {/* Programs & Resources */}
        <div className="mt-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 page-enter stagger-2">
            Programs & Support
          </h2>
          
          <div className="space-y-4">
            {resources.map((resource, index) => (
              <div 
                key={resource.title} 
                className={`card-soft page-enter stagger-${Math.min(index + 2, 4)}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-secondary rounded-2xl shrink-0">
                    <resource.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-foreground">
                        {resource.title}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        resource.status === 'Active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {resource.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {resource.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* External Resources */}
        <div className="mt-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 page-enter stagger-4">
            Connect & Learn More
          </h2>
          
          <div className="space-y-3">
            {externalResources.map((resource) => (
              <a
                key={resource.name}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card-soft block hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">{resource.name}</h3>
                    <p className="text-sm text-muted-foreground">{resource.description}</p>
                  </div>
                  <ExternalLink className="h-5 w-5 text-primary shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Contact PwSD */}
        <div className="mt-8 card-soft border-primary/30 page-enter stagger-4">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">
            Contact PwSD Association
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Persons with Silent Disabilities is a registered 501(c)(3) nonprofit organization. EIN: 33-1458854
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Phone className="h-4 w-4 text-primary" />
              <span className="text-sm">24/7 Info Line: <strong className="text-foreground">667.244.3966</strong></span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <Mail className="h-4 w-4 text-primary" />
              <a href="mailto:info@silent-disabilities.org" className="text-sm hover:text-primary">
                info@silent-disabilities.org
              </a>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Wellness tips and resources about invisible disabilities available in English and Spanish.
          </p>
        </div>

        {/* Support CTA */}
        <div className="mt-8 page-enter stagger-4">
          <a
            href="https://www.zeffy.com/donation-form/donate-to-make-a-difference-9694"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="btn-primary w-full">
              <Heart className="h-5 w-5 mr-2" />
              Support PwSD Association
            </Button>
          </a>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Contributions are tax-deductible to the extent permitted by law
          </p>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
